'use client';

import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { getTrySource, clearTrySource } from '../lib/tryNavigation';

/**
 * BackToPrevious — плавающая кнопка-стрелка в правом нижнем углу.
 *
 * Поведение:
 *  - Если юзер нажимал «Попробовать сейчас» в одной из сцен и в sessionStorage
 *    есть `trySource` — скролл возвращает в ту самую сцену + очищает storage.
 *  - Если trySource пустой — скролл наверх страницы.
 *  - Кнопка скрыта пока юзер у самого верха (scrollY ≤ 200).
 */

const VISIBLE_FROM = 200; // px от top, начиная с которого показываем кнопку

export default function BackToPrevious() {
  const reduced = useReducedMotion() ?? false;
  const [visible, setVisible] = useState(false);
  const [hasTrySource, setHasTrySource] = useState(false);

  useEffect(() => {
    const update = () => {
      setVisible(window.scrollY > VISIBLE_FROM);
      setHasTrySource(getTrySource() !== null);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const onClick = useCallback(() => {
    const source = getTrySource();
    if (source) {
      clearTrySource();
      setHasTrySource(false);
      document.getElementById(source)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const label = hasTrySource
    ? 'Вернуться к блоку, с которого начали'
    : 'Наверх страницы';

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={onClick}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 inline-flex items-center justify-center w-12 h-12 sm:w-13 sm:h-13 rounded-full bg-white/95 hover:bg-white border border-[#dce5d9] hover:border-[#b8c8b6] backdrop-blur-sm transition-colors"
          style={{
            boxShadow: '0 12px 32px -8px rgba(20,40,20,0.30), 0 0 0 1px rgba(255,255,255,0.5) inset',
          }}
          aria-label={label}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#161d16" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
